import React, { useState, FC } from 'react';
import { connect, useSelector } from 'react-redux';
import { MediaGallery } from 'common/components';
import { getProductVideos, Url, getProductImages, getDisplayedName } from 'features/product-info';
import { RootReducer } from 'store/root-reducer';
import { Dispatch } from 'redux';
import { cx } from 'linaria';
import { useDebounce } from '@navi-toolbox/hooks';
import { getIsBot } from 'common/selectors';
import { ActionBadgeProps, MediaList, MediaType, Hover } from './types';
import { PDP_MEDIA_LOADED, PDP_MEDIA_PREVIEW_CLICK, PDP_MEDIA_PREVIEW_HOVER } from './actions';
import { DATA_QA_DESKTOP_GALLERY } from './constants';
import { ThumbList } from './thumb-list';
import { Preview } from './preview';
import { MobileGallery } from './mobile-gallery';
import { styles } from './styles';
import { desktopGallerySsrSupport, mobileGallerySsrSupport, root } from './mobile-gallery/styles';
import { AdaptiveMediaGalleryContainer, MediaGalleryType } from '../adaptive-media-gallery-container';

type MediaGalleryMappedState = {
  images?: Url[];
  videos?: Url[];
  ActionBadge?: React.ComponentType<ActionBadgeProps>;
  displayedName: string;
};

const DELAY_HOVER = 200;

export type PdpMediaGalleryProps = MediaGalleryMappedState &
  React.HTMLAttributes<HTMLElement> & {
    onProductMediaPreviewClick?: (mediaType: string, contentLink: string) => void;
    onProductMediaPreviewHover?: (mediaType: string, contentLink: string) => void;
    onProductMediaLoaded?: (mediaType: string, contentLink: string, loadTime?: number) => void;
  };

export const ProductMediaGallery: FC<PdpMediaGalleryProps> = ({
  images,
  videos,
  ActionBadge,
  displayedName,
  className,
  onProductMediaPreviewClick,
  onProductMediaLoaded,
  onProductMediaPreviewHover,
  ...htmlProps
}) => {
  const commonImages = images?.map((media) => ({ type: 'image' as MediaType, media })) || [];
  const commonVideos = videos?.map((media) => ({ type: 'video' as MediaType, media })) || [];
  const commonMedia: MediaList = [...commonImages, ...commonVideos];
  const [active, setActive] = useState(0);
  const bot = useSelector(getIsBot);
  const [hover, setHover] = useState<Hover>(null);
  const hoverDebounced = useDebounce(hover, DELAY_HOVER);
  const activePreview = commonMedia.length ? commonMedia[hoverDebounced ?? active] : null;
  const key = activePreview?.type === 'video' ? undefined : active;
  const photoNumber = active !== 0 ? `${active + 1} ` : '';
  const altValue = `Фото ${photoNumber}${displayedName}`;
  const mediaUrl = activePreview?.media?.url ?? '';

  const handleSetActive = (val: number) => {
    setHover(() => null);
    setActive(() => val);
  };

  return (
    <>
      <MediaGallery className={className} {...htmlProps}>
        <AdaptiveMediaGalleryContainer bot={bot} type={MediaGalleryType.Mobile}>
          <MobileGallery
            data={commonMedia}
            active={active}
            displayedName={displayedName}
            style={cx(root, mobileGallerySsrSupport)}
            setActive={setActive}
            onClick={onProductMediaPreviewClick}
            onMediaLoad={onProductMediaLoaded}
          />
        </AdaptiveMediaGalleryContainer>
        <AdaptiveMediaGalleryContainer bot={bot} type={MediaGalleryType.Desktop}>
          <div
            className={cx(styles.desktopContainer, desktopGallerySsrSupport)}
            data-qa={DATA_QA_DESKTOP_GALLERY}
          >
            <ThumbList
              data={commonMedia}
              active={active}
              setActive={handleSetActive}
              setHover={setHover}
              onClick={onProductMediaPreviewClick}
              onHover={onProductMediaPreviewHover}
            />
            <Preview
              active={active}
              hover={hoverDebounced}
              key={key}
              alt={altValue}
              mediaList={commonMedia}
              videoList={commonVideos}
              setActive={handleSetActive}
              type={activePreview?.type}
              src={mediaUrl}
              onMediaLoad={onProductMediaLoaded}
            />
          </div>
        </AdaptiveMediaGalleryContainer>
      </MediaGallery>
    </>
  );
};

export const mapStoreToProps = (store: RootReducer): MediaGalleryMappedState => {
  const images = getProductImages(store);
  const videos = getProductVideos(store);
  const displayedName = getDisplayedName(store) ?? '';

  return {
    images,
    videos,
    displayedName,
  };
};

export const mapDispatchToProps = (
  dispatch: Dispatch,
): Pick<
  PdpMediaGalleryProps,
  'onProductMediaPreviewClick' & 'onProductMediaLoaded' & 'onProductMediaPreviewHover'
> => ({
  onProductMediaPreviewClick: (mediaType: string, contentLink: string) =>
    dispatch(PDP_MEDIA_PREVIEW_CLICK({ mediaType, contentLink })),
  onProductMediaPreviewHover: (mediaType: string, contentLink: string) =>
    dispatch(PDP_MEDIA_PREVIEW_HOVER({ mediaType, contentLink })),
  onProductMediaLoaded: (mediaType: string, contentLink: string, loadTime?: number) =>
    dispatch(PDP_MEDIA_LOADED({ mediaType, contentLink, loadTime })),
});

export const PdpMediaGallery = connect(mapStoreToProps, mapDispatchToProps)(ProductMediaGallery);
